import { fabric } from 'fabric';
import {
  type HasDraggableItems,
  type Constructor,
  type RecordId,
} from '../types';
import type DragReorderControl from '../controls/DragReorderControl';
import ItemPlacement from '../models/ItemPlacement';
import { type IPoint } from 'fabric/fabric-impl';

interface ItemDragBehaviors {
  drawDraggedItem: (ctx: CanvasRenderingContext2D) => void;
  calculatePlacement({
    midpoint,
    id,
    draggingVertical,
  }: {
    midpoint: IPoint;
    id: RecordId;
    draggingVertical: boolean;
  }): ItemPlacement;
  dragState: {
    left: number | undefined;
    top: number | undefined;
    placement: ItemPlacement | undefined;
  };
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
function ItemDragBehaviors<
  T extends Constructor<fabric.Object & HasDraggableItems<any>>
>(Base: T) {
  return class extends Base implements ItemDragBehaviors {
    __hasItemDragBehaviors = true;
    dragState: ItemDragBehaviors['dragState'] = {
      left: undefined,
      top: undefined,
      placement: undefined,
    };

    drawDraggedItem(ctx: CanvasRenderingContext2D) {
      const corner = this.__corner;
      if (
        !corner ||
        typeof corner === 'number' ||
        !corner.toLowerCase().includes('drag_') ||
        !this.isDragControl(corner)
      ) {
        // This should not happen as there must be a corner while dragging, but
        // (most likely) collab ops can remove the currently dragged obj. Early out
        // here to avoid errors
        return;
      }

      const { dragState } = this;
      const { id, draggingVertical } = this.controls[
        corner
      ] as DragReorderControl;

      // Draw the outline of the dragged item as it is being dragged
      if (dragState.left || dragState.top) {
        const rect = this.draggableItemForId({
          id,
          draggingVertical,
        });

        // Drawing the item currently being dragged
        if (rect) {
          const { width, height } = rect;
          const borderRadius = this.itemBorderRadius ?? 0;
          const x = dragState.left || 0 - width / 2;
          const y = dragState.top || 0 - height / 2;

          ctx.save();
          ctx.lineWidth = 2;
          ctx.strokeStyle = '#0073CF';
          if (ctx.roundRect && borderRadius) {
            ctx.beginPath();
            ctx.roundRect(x, y, width, height, borderRadius);
            ctx.closePath();
            ctx.stroke();
          } else {
            ctx.strokeRect(x, y, width, height);
          }
          ctx.restore();
        }
      }

      const adjustedPointer = fabric.util.transformPoint(
        { x: this.left, y: this.top } as fabric.Point,
        fabric.util.invertTransform(this.calcOwnMatrix())
      );

      if (dragState.placement) {
        let left, top;
        if (dragState.placement.position === 'TOP') {
          const offset = this.componentOffset();
          left = adjustedPointer.x + (draggingVertical ? 0 : offset.x);
          top = adjustedPointer.y + (draggingVertical ? offset.y : 0);
        } else if (dragState.placement.position === 'BOTTOM') {
          left = draggingVertical
            ? adjustedPointer.x
            : adjustedPointer.x + this.width;
          top = draggingVertical
            ? adjustedPointer.y + this.height
            : adjustedPointer.y;
        } else if (dragState.placement.target) {
          const rect = this.draggableItemForId({
            id: dragState.placement.target.id,
            draggingVertical,
          });
          if (rect) {
            if (dragState.placement.position === 'AFTER') {
              draggingVertical
                ? (rect.top += rect.height)
                : (rect.left += rect.width);
            }
            const itemOffset = fabric.util.transformPoint(
              { x: rect.left, y: rect.top } as fabric.Point,
              fabric.util.invertTransform(this.calcOwnMatrix())
            );
            left = draggingVertical ? adjustedPointer.x : itemOffset.x;
            top = draggingVertical ? itemOffset.y : adjustedPointer.y;
          }
        }
        ctx.save();
        ctx.fillStyle = '#0073CF';
        const indicatorWidth = 4;

        // Draw the destination indicator the dragged item
        ctx.fillRect(
          left - (draggingVertical ? 0 : indicatorWidth / 2),
          top - (draggingVertical ? indicatorWidth / 2 : 0),
          draggingVertical ? this.width : indicatorWidth,
          draggingVertical ? indicatorWidth : this.height
        );
        ctx.restore();
      }
    }

    isDragControl(corner) {
      return corner.toLowerCase().includes('drag_');
    }

    calculatePlacement({ midpoint, draggingVertical }) {
      const items = this.draggableItems({ draggingVertical });
      const contains = draggingVertical
        ? this.top <= midpoint.y && this.top + this.height >= midpoint.y
        : this.left <= midpoint.x && this.left + this.width >= midpoint.x;

      const target = items.find(item => {
        const { left, top, width, height } = item;
        if (draggingVertical) {
          return midpoint.y >= top && midpoint.y <= top + height;
        }
        return midpoint.x >= left && midpoint.x <= left + width;
      });

      // For some objects, items have additional separators that are not
      // included in the dimensions of the item. In these cases, prefer to keep
      // the existing placement rather than switching to the default.
      if (contains && this.dragState.placement && !target) {
        return this.dragState.placement;
      }

      // Default to top if dragging past start of the component, and to bottom if
      // dragging past the end of the component.
      let method = draggingVertical
        ? midpoint.y < this.top
          ? 'top'
          : 'bottom'
        : midpoint.x < this.left
        ? 'top'
        : 'bottom';
      if (target) {
        method = 'after';
        const percentage = draggingVertical
          ? (midpoint.y - target.top) / target.height
          : (midpoint.x - target.left) / target.width;
        method = percentage > 0.5 ? 'after' : 'before';
      }

      return ItemPlacement[method](target?.item);
    }

    _render(ctx: CanvasRenderingContext2D) {
      super._render(ctx);
      this.drawDraggedItem(ctx);
    }
  };
}

export default ItemDragBehaviors;
